import { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import { AppDispatch } from "@/app/store";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { trackPodcastFiltersChanged } from "@/modules/hub/features/Podcasts/analytics/podcastsAnalytics";
import {
  fetchResources,
  fetchMoreResources,
  setResources,
  setPage,
  setHasMore,
  setScrollPosition,
  setLastFetchParams,
  setSearchInput,
  setShowOnlyFavorites,
  setShowOnlyFeatured,
  setSelectedTrades,
  setSelectedResourceCategories,
  setSelectedBusinessRoles,
  setIsRestoringState,
  clearFilters,
} from "../slices/podcastsSlice";
import { getPodcastsMetadataAction } from "../slices/podcastsMetadataSlice";
import {
  PodcastsFilterFormData,
  PodcastsFilterFormSchema,
} from "./PodcastsFilterFormSchema";
import {
  Trade,
  BusinessRole,
} from "@/modules/hub/features/ResourceLibrary/api/getResourceLibraryMetadata/types";
import { ResourceCategory } from "@/api/fetchCreateUpdateResourceMetadata/types";
import { getFavoritesKey } from "@/api/getFavoritesKey/getFavoritesKeyApi";
import { searchService } from "@/modules/hub/features/Search/services/searchService";

export function usePodcasts() {
  const dispatch = useDispatch<AppDispatch>();

  const {
    resources,
    page,
    hasMore,
    totalCount,
    lastFetchParams,
    scrollPosition,
    searchInput,
    showOnlyFavorites,
    showOnlyFeatured,
    selectedTrades,
    selectedResourceCategories,
    selectedBusinessRoles,
    isFetching,
    availableFacets,
  } = useSelector((state: RootState) => state.podcasts);

  const { metadata, loadingGet } = useSelector(
    (state: RootState) => state.podcastsMetadata,
  );

  const form = useForm<PodcastsFilterFormData>({
    resolver: zodResolver(PodcastsFilterFormSchema),
    defaultValues: {
      searchTerm: searchInput || "",
      isFavoriteOnly: showOnlyFavorites || false,
      isFeaturedOnly: showOnlyFeatured || false,
      trades: selectedTrades || [],
      categories: selectedResourceCategories || [],
      businessRoles: selectedBusinessRoles || [],
    },
    mode: "onChange",
  });

  const debouncedSearchTerm = useDebouncedValue(
    form.watch("searchTerm") ?? "",
    500,
  );

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [favoriteUuids, setFavoriteUuids] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const hasTrackedInitialFilters = useRef(false);

  type SortOption = "newest" | "oldest" | "mostViewed" | "alphabetical";
  const [sortOption, setSortOption] = useState<SortOption>("newest");

  const sortMappings: Record<
    SortOption,
    { sortBy: string; sortOrder: string }
  > = {
    newest: { sortBy: "publishStartDate", sortOrder: "desc" },
    oldest: { sortBy: "publishStartDate", sortOrder: "asc" },
    mostViewed: { sortBy: "viewCount", sortOrder: "desc" },
    alphabetical: { sortBy: "title", sortOrder: "asc" },
  };

  useEffect(() => {
    const subscription = form.watch((values) => {
      dispatch(setSearchInput(values.searchTerm || ""));
      dispatch(setShowOnlyFavorites(values.isFavoriteOnly || false));
      dispatch(setShowOnlyFeatured(values.isFeaturedOnly || false));
      dispatch(
        setSelectedTrades((values.trades ?? []).filter((t): t is Trade => !!t)),
      );
      dispatch(
        setSelectedResourceCategories(
          (values.categories ?? []).filter((c): c is ResourceCategory => !!c),
        ),
      );
      dispatch(
        setSelectedBusinessRoles(
          (values.businessRoles ?? []).filter((e): e is BusinessRole => !!e),
        ),
      );
    });
    return () => subscription.unsubscribe();
  }, [form, dispatch]);

  const buildRequestData = useCallback(() => {
    const values = form.getValues();
    const { sortBy, sortOrder } = sortMappings[sortOption];

    return {
      searchTerm: values.searchTerm?.trim() || undefined,
      showFavorites: values.isFavoriteOnly || undefined,
      showFeatured: values.isFeaturedOnly || undefined,
      tradeIds: values.trades?.map((t) => t.id),
      categoryIds: values.categories?.map((c) => c.id),
      businessRoleIds: values.businessRoles?.map((e) => e.id),
      // Pass the actual names for Algolia filtering
      tradeNames: values.trades?.map((t) => t.name),
      categoryNames: values.categories?.map((c) => c.name),
      businessRoleNames: values.businessRoles?.map((e) => e.name),
      sortBy,
      sortOrder,
      page: 1,
      pageSize: 10,
    };
  }, [form, sortOption]);

  const isStateRestorable = useCallback(() => {
    if (!lastFetchParams) return false;

    const getFiltersWithoutPagination = (params: Record<string, unknown>) => {
      const paginationKeys = ["page", "pageSize"];
      return Object.fromEntries(
        Object.entries(params).filter(([key]) => !paginationKeys.includes(key)),
      );
    };

    const currentFilters = getFiltersWithoutPagination(buildRequestData());
    const previousFilters = getFiltersWithoutPagination(lastFetchParams);

    return JSON.stringify(currentFilters) === JSON.stringify(previousFilters);
  }, [lastFetchParams, buildRequestData]);

  useEffect(() => {
    dispatch(getPodcastsMetadataAction());
  }, [dispatch]);

  // Fetch favorites on mount
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const favoritesData = await getFavoritesKey("podcasts");
        setFavoriteUuids(new Set(favoritesData.favoriteResourceUuids));
        searchService.updateFavoritesKey(
          favoritesData.favoritesSecuredApiKey,
          favoritesData.favoriteResourceUuids,
        );
      } catch (error) {
        console.error("[Podcasts] Error fetching favorites:", error);
      }
    };

    fetchFavorites();
  }, []);

  // Function to refresh favorites (called after toggle)
  const refreshFavorites = useCallback(async () => {
    try {
      const favoritesData = await getFavoritesKey();

      // Update local state with server data
      setFavoriteUuids(new Set(favoritesData.favoriteResourceUuids));

      // Update search service with new key
      searchService.updateFavoritesKey(
        favoritesData.favoritesSecuredApiKey,
        favoritesData.favoriteResourceUuids,
      );

      return favoritesData;
    } catch (error) {
      console.error("[Podcasts] Error refreshing favorites:", error);
      throw error;
    }
  }, []);

  useEffect(() => {
    if (!isInitialLoading && scrollPosition > 0) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPosition, behavior: "auto" });
      });
    }
  }, [isInitialLoading, scrollPosition]);

  useEffect(() => {
    const saveScroll = () => {
      dispatch(setScrollPosition(window.scrollY));
    };
    window.addEventListener("beforeunload", saveScroll);
    window.addEventListener("pagehide", saveScroll);
    return () => {
      saveScroll();
      window.removeEventListener("beforeunload", saveScroll);
      window.removeEventListener("pagehide", saveScroll);
    };
  }, [dispatch]);

  useEffect(() => {
    const load = async () => {
      // Wait for metadata to load first
      if (!metadata) {
        return;
      }

      if (resources.length > 0 && lastFetchParams && isStateRestorable()) {
        dispatch(setIsRestoringState(true));
        setIsInitialLoading(false);
        return;
      }

      const request = buildRequestData();
      setLoading(true);
      dispatch(setResources([]));
      dispatch(setPage(1));
      dispatch(setHasMore(true));

      await dispatch(fetchResources(request));

      dispatch(setLastFetchParams(request));
      setLoading(false);
      setIsInitialLoading(false);
    };
    load();
  }, [metadata]);

  // Create stable values for watch dependencies
  const isFavoriteOnly = form.watch("isFavoriteOnly");
  const isFeaturedOnly = form.watch("isFeaturedOnly");
  const trades = form.watch("trades");
  const categories = form.watch("categories");
  const businessRoles = form.watch("businessRoles");
  const searchTerm = form.watch("searchTerm");

  useEffect(() => {
    if (isInitialLoading || !metadata) return;

    const handleFilterChange = async () => {
      const values = form.getValues();

      // If favorites filter is being turned ON, refresh favorites key first
      if (values.isFavoriteOnly) {
        try {
          const favoritesData = await getFavoritesKey("podcasts");
          setFavoriteUuids(new Set(favoritesData.favoriteResourceUuids));
          searchService.updateFavoritesKey(
            favoritesData.favoritesSecuredApiKey,
            favoritesData.favoriteResourceUuids,
          );
        } catch (error) {
          console.error("[Podcasts] Failed to refresh favorites key:", error);
        }
      }

      const request = buildRequestData();
      setLoading(true);
      dispatch(setResources([]));
      dispatch(setPage(1));
      dispatch(setHasMore(true));
      await dispatch(fetchResources(request));
      dispatch(setLastFetchParams(request));
      setLoading(false);
    };

    handleFilterChange();
  }, [
    isFavoriteOnly,
    isFeaturedOnly,
    JSON.stringify(trades),
    JSON.stringify(categories),
    JSON.stringify(businessRoles),
    searchTerm,
    sortOption,
  ]);

  useEffect(() => {
    if (isInitialLoading) return;

    if (!hasTrackedInitialFilters.current) {
      hasTrackedInitialFilters.current = true;
      return;
    }

    trackPodcastFiltersChanged();
  }, [
    debouncedSearchTerm,
    form.watch("isFavoriteOnly"),
    form.watch("isFeaturedOnly"),
    form.watch("trades"),
    form.watch("categories"),
    form.watch("businessRoles"),
    isInitialLoading,
  ]);

  const fetchMore = useCallback(() => {
    const nextPage = page + 1;
    const request = { ...buildRequestData(), page: nextPage };
    dispatch(setPage(nextPage));
    dispatch(fetchMoreResources(request));
  }, [page, dispatch, buildRequestData]);

  const handleClearFilters = () => {
    dispatch(clearFilters());

    form.reset({
      searchTerm: "",
      isFavoriteOnly: false,
      isFeaturedOnly: false,
      trades: [],
      categories: [],
      businessRoles: [],
    });
  };

  const setFeaturedOnly = () => {
    form.setValue("isFeaturedOnly", true);
  };

  // Use dynamic facets from search results, fall back to static metadata if no search has been done yet
  const dynamicMetadata = availableFacets
    ? {
        trades: availableFacets.trades.map((t) => ({
          id: t.id,
          name: t.name,
          icon: t.icon,
          count: t.count,
        })),
        businessRoles: availableFacets.businessRoles.map((b) => ({
          id: b.id,
          name: b.name,
          description: b.description,
          count: b.count,
        })),
        resourceCategories: availableFacets.categories.map((c) => ({
          id: c.id,
          name: c.name,
          count: c.count,
        })),
        // Podcasts don't need resourceTypes in filter (always Podcast)
        resourceTypes: metadata?.resourceTypes || [],
      }
    : metadata;

  // Merge favorite status with resources
  const resourcesWithFavorites = resources.map((resource) => ({
    ...resource,
    isFavorited: favoriteUuids.has(resource.uuid),
  }));

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback(
    async (resourceUuid: string) => {
      // Optimistic update
      const newFavorites = new Set(favoriteUuids);
      const wasInFavorites = newFavorites.has(resourceUuid);

      if (wasInFavorites) {
        newFavorites.delete(resourceUuid);
      } else {
        newFavorites.add(resourceUuid);
      }
      setFavoriteUuids(newFavorites);

      // API call happens in ResourceCard - wait for it to complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      // ALWAYS refresh favorites key after toggle
      try {
        await refreshFavorites();

        // If favorites filter is active, re-fetch resources with new key
        if (isFavoriteOnly) {
          const params = buildRequestData();
          dispatch(fetchResources(params));
        }
      } catch (error) {
        console.error("[Podcasts] Error refreshing favorites:", error);
        // Revert optimistic update on error
        setFavoriteUuids(favoriteUuids);
      }
    },
    [
      favoriteUuids,
      isFavoriteOnly,
      refreshFavorites,
      buildRequestData,
      dispatch,
    ],
  );

  return {
    form,
    page,
    resources: resourcesWithFavorites,
    hasMore,
    totalCount,
    loading: loading || isFetching,
    isInitialLoading,
    fetchMore,
    handleClearFilters,
    setFeaturedOnly,
    sidebarMetadata: dynamicMetadata,
    isMetadataLoading: loadingGet && !availableFacets,
    sortOption,
    setSortOption,
    handleFavoriteToggle,
    refreshFavorites,
  };
}
